// frontend/src/components/Home/Album.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, Upload, Heart, Trophy, ChevronsRight, User, Camera as CameraIcon } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import './Album.css';

const Album = () => {
    const { t } = useTranslation();
    const [showNotice, setShowNotice] = useState(false);
    const [noticeText, setNoticeText] = useState('');
    const [topPhotos, setTopPhotos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [topPhotosLimit, setTopPhotosLimit] = useState(20); // Default to 20
    const navigate = useNavigate();

    // Fetch top photos
    useEffect(() => {
        const fetchTopPhotos = async () => {
            try {
                setLoading(true);
                // Get photos from the new top photos API - will automatically use the configured limit
                const response = await api.get('/photos/top');
                
                if (response.data && response.data.photos) {
                    // Take first 6 photos from the top photos
                    const photos = response.data.photos.slice(0, 6);
                    setTopPhotos(photos);
                }
                
                // Store the limit from the server for display
                if (response.data && response.data.limit) {
                    setTopPhotosLimit(response.data.limit);
                }
                
                // Check if there's a notice about client-side sorting or other issues
                if (response.data && response.data.notice) {
                    console.log('Notice from API:', response.data.notice);
                    // Show the notice to users in development mode or if it's important
                    if (response.data.notice.includes('index') || 
                        response.data.notice.includes('performance') ||
                        response.data.notice.includes('temporarily')) {
                        setNoticeText(response.data.notice);
                        setShowNotice(true);
                    }
                }
            } catch (err) {
                console.error('Error fetching top photos:', err);
                
                // Try fallback regardless of error type
                try {
                    console.log('Attempting fallback to regular photos API');
                    const fallbackResponse = await api.get('/photos?limit=6');
                    if (fallbackResponse.data && fallbackResponse.data.photos) {
                        console.log('Fallback successful, got', fallbackResponse.data.photos.length, 'photos');
                        setTopPhotos(fallbackResponse.data.photos);
                        
                        // Show notice that these aren't actually "top" photos
                        setNoticeText(t('photoGallery.fallbackPhotosNotice'));
                        setShowNotice(true);
                        setError(null); // Clear error since we have fallback photos
                    } else {
                        setError(t('photoGallery.loadError'));
                    }
                } catch (fallbackErr) {
                    console.error('Error fetching fallback photos:', fallbackErr);
                    setError(t('photoGallery.loadError'));
                }
            } finally {
                setLoading(false);
            }
        };
        
        fetchTopPhotos();
    }, [t]);

    const handleUploadClick = () => {
        navigate('/photos');
    };

    // id: the hero's wedding slide scrolls here while that event has no video
    // of its own (see src/config/eventVideos.js).
    return (
        <section id="wedding-album" className="album-section">
            {showNotice && (
                <div className="notice-container">
                    <div className="notice-content">
                        {noticeText || t('album.developmentNotice')}
                    </div>
                </div>
            )}

            <div className="album-container">
                <div className="album-header">
                    <CameraIcon className="album-icon" size={32} />
                    <h2 className="album-title">{t('album.title')}</h2>
                    <p className="album-description">{t('album.description')}</p>
                </div>

                <div className="top-photos-section">
                    <div className="top-photos-header">
                        <div className="top-photos-title">
                            <Trophy size={20} className="trophy-icon" />
                            <Link to="/photos/top" className="top-photos-link">
                                {t('photoGallery.topPhotos', { count: topPhotosLimit })}
                                <ChevronsRight size={16} className="chevron-icon" />
                            </Link>
                        </div>
                        <button
                            className="upload-button"
                            onClick={handleUploadClick}
                            aria-label={t('photoUpload.uploadButton')}
                        >
                            <Upload size={18} className="upload-icon-button" />
                            {t('photoUpload.uploadButton')}
                        </button>
                    </div>
                    
                    {loading ? (
                        <div className="top-photos-loading">
                            <div className="spinner"></div>
                            <p>{t('loading')}</p>
                        </div>
                    ) : error ? (
                        <div className="top-photos-error">
                            <p>{error}</p>
                        </div>
                    ) : topPhotos.length === 0 ? (
                        <div className="top-photos-empty">
                            <p>{t('photoGallery.noPhotos')}</p>
                            <p>{t('photoGallery.beFirstToUpload')}</p>
                        </div>
                    ) : (
                        <div className="top-photos-grid">
                            {topPhotos.map((photo, index) => (
                                <div key={photo.id} className="top-photo-card">
                                    <div className="top-photo-rank">#{index + 1}</div>
                                    <Link to="/photos" className="top-photo-link">
                                        <div className="top-photo-image">
                                            <img
                                                src={photo.imageUrl}
                                                alt={photo.description || t('photoGallery.photoBy', { name: photo.userName })}
                                            />
                                        </div>
                                        <div className="top-photo-details">
                                            <p className="top-photo-user">
                                                <Image size={14} className="camera-icon" />
                                                <span>© {photo.userName}</span>
                                            </p>
                                            <p className="top-photo-likes">
                                                <Heart size={16} className="like-icon" fill="#dc3545" stroke="#dc3545" /> 
                                                {photo.likeCount || 0}
                                            </p>
                                        </div>
                                    </Link>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="photo-guidelines">
                    <h3 className="guidelines-title">{t('album.guidelinesTitle')}</h3>
                    <ul className="guidelines-list">
                        <li>{t('album.guideline1')}</li>
                        <li>{t('album.guideline2')}</li>
                        <li>{t('album.guideline3')}</li>
                    </ul>
                </div>
            </div>
        </section>
    );
};

export default Album;